import type { Column, DeepKeys, DeepValue, RowData } from "@tanstack/vue-table";
import { computed, toValue, type MaybeRefOrGetter } from "vue";
import type { FacetedKeyOptions } from "./faceted-types";

export function useFilterableColumn<TData extends RowData, TKey extends DeepKeys<TData>>(
	column: MaybeRefOrGetter<Column<TData, DeepValue<TData, TKey>>>,
	options?: MaybeRefOrGetter<FacetedKeyOptions<TData, TKey>>
) {
	type PossibleValues = DeepValue<TData, TKey>;

	function optionDisplay(option: PossibleValues): string {
		return toValue(options)?.choices?.[option]?.name || option;
	}

	function optionHint(option: PossibleValues): string | null {
		return toValue(options)?.choices?.[option]?.hint || null;
	}

	function optionsSort(a: string, b: string) {
		const aIdx = optionsWeightMap.value.get(a) ?? -1;
		const bIdx = optionsWeightMap.value.get(b) ?? -1;

		// One of them is not indexed, simply sort by text
		if (aIdx === -1 || bIdx === -1) {
			return a.localeCompare(b);
		}

		return aIdx - bIdx;
	}

	const selectedValues = computed(() => {
		const values = (toValue(column).getFilterValue() as string[]) || [];

		return values.sort(optionsSort);
	});
	const selectedCount = computed(() => selectedValues.value.length);

	const facets = computed<Map<any, number>>(() => {
		const facets = toValue(column).getFacetedUniqueValues() || new Map();

		for (const selected of selectedValues.value) {
			if (!facets.has(selected)) {
				facets.set(selected, 0);
			}
		}

		return facets;
	});

	const optionsWeightMap = computed(() => {
		const idxLookup = new Map<string, number>();

		const optsVal = toValue(options);

		if (optsVal?.choices == null) {
			return idxLookup;
		}

		for (const [idx, key] of Object.keys(optsVal.choices).entries()) {
			idxLookup.set(key, idx);
		}

		return idxLookup;
	});

	const choices = computed(() => {
		const options = Array.from(facets.value.keys()).sort(optionsSort) as string[];

		return options.map((opt) => ({
			label: optionDisplay(opt as any),
			hint: optionHint(opt as any),
			value: opt,
		}));
	});

	const title = computed(() => {
		const optsVal = toValue(options);

		if (optsVal?.name != null) {
			return optsVal.name;
		}

		const colVal = toValue(column);

		return typeof colVal.columnDef.header === "string" ? colVal.columnDef.header : colVal.id;
	});

	return {
		title,
		choices,
		facets,
		selectedValues,
		selectedCount,
		optionDisplay,
		optionHint,
	};
}
